import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Redirect } from "react-router-dom";

import {
  LOGOUT_SUCCESS,
  getIsCiamAuthenticated,
  getRefreshToken,
  getToken,
} from "data-handler/ducks/auth";
import { takeApproverReturn } from "helpers/approverReturn";
import { isJwtExpired } from "helpers/auth";

/**
 * Renders the given `component` if logged in, else a Redirect to login
 */
export default ({ component: Component, ...rest }) => {
  const dispatch = useDispatch();
  const JWTToken = useSelector(getToken);
  const refreshToken = useSelector(getRefreshToken);
  // A CIAM session lives in an httpOnly cookie the browser cannot read, so
  // there is no JWT here to inspect - the backend owns its expiry and surfaces
  // the end of it as a 401, which the interceptor turns into a logout.
  const isCiamAuthenticated = useSelector(getIsCiamAuthenticated);

  // An expired access token is only a dead end when there is no usable
  // refresh token to trade it in with: the axios interceptor refreshes
  // silently on the first 401. Non-JWT tokens (local logins, opaque refresh
  // tokens) are never treated as expired.
  const canRecoverSession = refreshToken && !isJwtExpired(refreshToken);
  const isSessionExpired =
    !isCiamAuthenticated && isJwtExpired(JWTToken) && !canRecoverSession;

  // Clear the dead session so its token stops being sent with requests
  useEffect(() => {
    if (isSessionExpired) {
      dispatch({ type: LOGOUT_SUCCESS });
    }
  }, [dispatch, isSessionExpired]);

  const isUserAuthenticated =
    isCiamAuthenticated || (JWTToken !== undefined && !isSessionExpired);
  const isUserLocalAuthenticated =
    isCiamAuthenticated || (JWTToken && !isSessionExpired);

  // A user the approver app sent here to sign in goes back to it. This also
  // covers the return from CIAM, because LoginRedirect pushes "/".
  useEffect(() => {
    if (isUserAuthenticated && isUserLocalAuthenticated) {
      const target = takeApproverReturn();
      if (target) {
        // The approver lives outside this app, so this is a full navigation
        window.location.replace(target);
      }
    }
  }, [isUserAuthenticated, isUserLocalAuthenticated]);
  return (
    <Route
      {...rest}
      render={(props) =>
        isUserAuthenticated && isUserLocalAuthenticated ? (
          <Component {...props} {...rest} />
        ) : isUserAuthenticated && !isUserLocalAuthenticated ? (
          <Redirect
            to={{
              pathname: "/locallogin/",
              state: { from: props.location },
            }}
          />
        ) : (
          <Redirect
            to={{
              pathname: "/login/",
              state: { from: props.location },
            }}
          />
        )
      }
    />
  );
};
